import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { listCalendarEventTypes, type CalendarEventType } from '../lib/calendar-api.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { CalendarEventForm } from './calendar-event-form.js';

/**
 * Záložka/panel „Termíny zakázky" (krok CRM3, zadání bod 2: „vytvoření
 * … i ze zakázky").
 *
 * Vzorek je `case-demands-panel.tsx` (CRM1): panel v detailu zakázky, který
 * si tahá co potřebuje ze svého kontextu. Vazba `caseId` je PŘEDVYPLNĚNÁ a
 * schválná — termín v kalendáři musí patřit této zakázce, jinak by se
 * „termíny této zakázky" daly plnit cizími.
 *
 * Záměrně NENÍ náhradou záložky „Prohlídky" v detailu zakázky (ta zůstává
 * `preparing` do CRM4): to je seznam PROHLÍDEK jako prohlídky — co se
 * jelo, s hodnocením a vyúčtováním. Tady je termín jako položka kalendáře.
 */
export function CaseEventsPanel(props: {
  readonly caseId: string;
  readonly caseTitle?: string | undefined;
}): React.JSX.Element {
  const t = useT();
  const { caseId } = props;
  const [types, setTypes] = React.useState<CalendarEventType[]>([]);
  const [members, setMembers] = React.useState<TenantMember[]>([]);
  const [showForm, setShowForm] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void Promise.all([listCalendarEventTypes(), listMembers()])
      .then(([nextTypes, nextMembers]) => {
        if (cancelled) return;
        setTypes(nextTypes);
        setMembers(nextMembers);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="case-events-panel">
      <p className="mb-2 text-sm">{t('webAdmin.calendar.drawer.heading')}</p>
      {notice !== null && (
        <p role="status" className="mb-2 text-sm" data-testid="case-events-notice">
          {notice}
        </p>
      )}
      {!showForm && (
        <button
          type="button"
          className="rh-btn"
          data-testid="case-events-new"
          onClick={() => setShowForm(true)}
        >
          {t('webAdmin.calendar.newEvent')}
        </button>
      )}
      {showForm && types.length > 0 && (
        <CalendarEventForm
          types={types}
          members={members}
          seed={{ caseId, eventType: 'viewing' }}
          onCreated={() => {
            setShowForm(false);
            setNotice(t('webAdmin.calendar.form.created'));
          }}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
